import { api } from "./api";
import { enqueue } from "./offlineOutbox";
import { useOnlineStore } from "../store/onlineStore";

/**
 * Attempts an axios mutation. If the network is unreachable (offline / DNS /
 * 502 timeout — anything WITHOUT a HTTP response), the request is persisted
 * to the IndexedDB outbox and the caller receives a synthetic optimistic
 * payload. HTTP 4xx/5xx responses fall through as normal errors so real
 * validation failures are still surfaced.
 *
 * Usage:
 *   const { data, offline } = await offlineMutate({
 *     url: "/tasks",
 *     method: "POST",
 *     body: taskPayload,
 *     buildOptimistic: () => ({ id: `temp_${crypto.randomUUID()}`, ..., _pending: true }),
 *   });
 */
export async function offlineMutate({ url, method, body, buildOptimistic }) {
  const online = useOnlineStore.getState().online;
  if (online) {
    try {
      const res = await api.request({ url, method, data: body });
      return { data: res.data, offline: false, replayed: false };
    } catch (err) {
      const isNetworkError = !err.response;
      if (!isNetworkError) throw err; // real 4xx/5xx — bubble up
      // Fall through into offline enqueue.
      useOnlineStore.getState().setOnline(false);
    }
  }
  const token = localStorage.getItem("taskora_access_token") || null;
  const entry = { url, method, body, token };
  const stored = await enqueue(entry);
  await useOnlineStore.getState().refreshCount();
  const optimistic = buildOptimistic
    ? buildOptimistic()
    : { ...body, _pending: true, _outbox_id: stored.id };
  optimistic._pending = true;
  optimistic._outbox_id = stored.id;
  return { data: optimistic, offline: true, replayed: false };
}

/** Generate a client-side temp id for optimistic records. */
export function tempId(prefix = "temp") {
  const rnd =
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID().replace(/-/g, "").slice(0, 16)
      : Math.random().toString(36).slice(2, 18);
  return `${prefix}_${rnd}`;
}
